import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import type { ConsentState, ConsentStatus } from "@/frontend/types/consent";
import { canRetryConsentLookup } from "@/frontend/utils/consent";

/**
 * Gives the consent status and the function that records the acceptance. Every
 * entry point goes through this hook, so a lookup that fails is never treated
 * as accepted.
 *
 * @returns the consent state and the callbacks the screens need
 */
export function useConsent(): ConsentState {
  const queryClient = useQueryClient();
  const [isExitOpen, setExitOpen] = useState(false);
  const [isSetupClosed, setIsSetupClosed] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);

  // `{}` is deliberate. The server finds the membership from the actor. Do not
  // pass patientMembershipId here. This hook is what keeps it optional.
  const termsQuery = useQuery(orpc.onboarding.getTermsAcceptance.queryOptions({ input: {} }));
  const acceptMutation = useMutation(orpc.onboarding.acceptTerms.mutationOptions());

  const accepted = justAccepted || (termsQuery.data?.accepted ?? false);

  // A lookup that fails never reports `accepted`. Thus it cannot let a patient
  // go past consent.
  const status: ConsentStatus = accepted
    ? "accepted"
    : termsQuery.isLoading
      ? "loading"
      : termsQuery.isError
        ? canRetryConsentLookup(termsQuery.error)
          ? "error"
          : "blocked"
        : "needed";

  return {
    status,
    isSetupClosed,
    isExitOpen,
    setExitOpen,
    closeSetup: () => {
      setExitOpen(false);
      setIsSetupClosed(true);
    },
    accept: () =>
      acceptMutation.mutate(
        {},
        {
          onSuccess: () => {
            setJustAccepted(true);
            queryClient.invalidateQueries({ queryKey: orpc.onboarding.getTermsAcceptance.key() });
          },
        },
      ),
    isAccepting: acceptMutation.isPending,
    acceptFailed: acceptMutation.isError,
    retry: () => {
      termsQuery.refetch();
    },
    isRetrying: termsQuery.isFetching,
  };
}
